import { type DependencyList, type EffectCallback, type useEffect, type useLayoutEffect, useRef } from 'react';

import isEqual from 'fast-deep-equal/react';

import { filterNotDefined } from '@enterprise/core';

import type { ElementTarget } from '#types/element-target.type';
import type { Target } from '#types/target.type';

import { useDidUnmount } from '#life-cycle/did-unmount.hook';
import { getTargetElement } from '#utilities/get-target-element.util';

/**
 * Factory function to create a custom hook that runs an effect with dependencies and target elements.
 *
 * This utility allows you to specify either `useEffect` or `useLayoutEffect` as the effect type,
 * and ensures the effect is re-executed only when the dependencies or the resolved target elements change.
 * It also handles cleanup on component unmount.
 *
 * @template TElement - The type of the target element.
 * @param useEffectType - The React effect hook to use (`useEffect` or `useLayoutEffect`).
 * @returns A hook function that accepts an effect callback, a dependency list, and one or more target elements.
 *
 * @example
 * const useEffectWithTarget = createEffectWithTarget(useEffect);
 * useEffectWithTarget(() => { ... }, [dep1, dep2], targetRef);
 */
export const createEffectWithTarget = <TElement extends ElementTarget>(
  useEffectType: typeof useEffect | typeof useLayoutEffect
) => {
  const useEffectWithTarget = (
    effect: EffectCallback,
    dependencies: DependencyList,
    target: Target<TElement> | Target<TElement>[]
  ) => {
    const hasInitRef = useRef(false);
    const lastDepsRef = useRef<DependencyList>([]);
    const lastElementRef = useRef<(ElementTarget | null)[]>([]);
    // biome-ignore lint/suspicious/noExplicitAny: unsure about the type here
    const unLoadRef = useRef<any>(undefined);

    useEffectType(() => {
      const targets = Array.isArray(target) ? target : [target];
      const elements = filterNotDefined(
        targets.map((item) => {
          return getTargetElement(item);
        })
      );

      if (!hasInitRef.current) {
        hasInitRef.current = true;
        lastElementRef.current = elements;
        lastDepsRef.current = dependencies;
        unLoadRef.current = effect();

        return;
      }

      if (
        elements.length !== lastElementRef.current.length ||
        !isEqual(lastElementRef.current, elements) ||
        !isEqual(lastDepsRef.current, dependencies)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        unLoadRef.current?.();
        lastElementRef.current = elements;
        lastDepsRef.current = dependencies;
        unLoadRef.current = effect();
      }
    });

    useDidUnmount(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      unLoadRef.current?.();
      hasInitRef.current = false;
    });
  };

  return useEffectWithTarget;
};
